import type { Account, ChannelsResponse } from '../types/app';

interface RequestOptions {
  method?: string;
}

const requestJson = <T>(
  url: string,
  options: RequestOptions = { method: 'GET' },
): Promise<T> => {
  if (window.fetch) {
    return window.fetch(url, options).then((response) => {
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      return response.json() as Promise<T>;
    });
  }

  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(options.method || 'GET', url);
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        try {
          resolve(JSON.parse(request.responseText) as T);
        } catch {
          reject(new Error('The server returned invalid JSON'));
        }
      } else {
        reject(new Error(`Request failed with status ${request.status}`));
      }
    };
    request.onerror = () => reject(new Error('Network request failed'));
    request.send();
  });
};

let accountsRequest: Promise<Account[]> | undefined;
const channelsRequests = new Map<string, Promise<ChannelsResponse>>();

export const getAccounts = (): Promise<Account[]> => {
  if (!accountsRequest) {
    accountsRequest = requestJson<Account[]>(
      'https://assets.internal.inthegame.io/general/list_demos_ott.json',
    ).catch((error: unknown) => {
      accountsRequest = undefined;
      throw error;
    });
  }

  return accountsRequest;
};

export const getChannels = (
  accountId: string,
  environment: string,
): Promise<ChannelsResponse> => {
  const cacheKey = `${environment}:${accountId}`;
  const existingRequest = channelsRequests.get(cacheKey);
  if (existingRequest) return existingRequest;

  const host =
    environment === 'dev' || environment === 'stage'
      ? 'https://openapi.internal.inthegame.io'
      : 'https://openapi.inthegame.io';
  const url = `${host}/${encodeURIComponent(environment)}/api/v2/account?accountId=${encodeURIComponent(accountId)}`;
  const request = requestJson<ChannelsResponse>(url).catch((error: unknown) => {
    channelsRequests.delete(cacheKey);
    throw error;
  });

  channelsRequests.set(cacheKey, request);
  return request;
};
